import { useEffect, type ReactNode } from 'react'
import { Link } from 'react-router'

import { sendEmergencyHelpEvent } from '../../api/emergencyHelp'
import { Icon } from '../../components/Icon'
import {
  PAGE_GUTTER_DESKTOP,
  PageBand,
  PageShell,
} from '../../components/PageShell'
import { EMERGENCY_NUMBER } from '../../lib/emergencyNumber'
import { STEP_COUNT } from './paths'

/**
 * The chrome shared by Emergency Help screens 1–4 (M35-T06, M36-T08, spec
 * §C.1): a red band reading "Medcor · Emergency help", the step, and a 112
 * pill that is the FIRST focusable control on every screen. Nothing the page
 * loads can come before it or delay it.
 *
 * Public since M36's switch-over: no `noindex` (M36-T11).
 */

const call112 = () => sendEmergencyHelpEvent({ kind: 'called_112' })

/**
 * `opened` counts a person entering the flow, not a visit to one screen: once
 * per browser tab, from whichever screen they arrive on (Back to screen 1
 * does not count again; a direct link to a hospital still counts). The tab
 * flag holds no personal data, only "opened". The module flag covers storage
 * being blocked, and React's development double-run of effects.
 */
const OPENED_KEY = 'medcor.emergency-help.opened'
let openedThisPage = false
function countOpenedOnce(): void {
  if (openedThisPage) return
  openedThisPage = true
  try {
    if (window.sessionStorage.getItem(OPENED_KEY) === '1') return
    window.sessionStorage.setItem(OPENED_KEY, '1')
  } catch {
    // Storage blocked: count once for this page's lifetime.
  }
  sendEmergencyHelpEvent({ kind: 'opened' })
}

export function EmergencyHelpShell({
  step,
  title,
  children,
}: {
  step: 1 | 2 | 3 | 4
  /** The tab title, which a screen reader announces on arrival. */
  title: string
  children: ReactNode
}): ReactNode {
  useEffect(countOpenedOnce, [])
  return (
    <PageShell measure={640} as="main">
      <title>{`${title} · Emergency help · Medcor`}</title>
      <header
        className={`flex items-center gap-2 bg-sos px-3 py-2 text-sos-bg ${PAGE_GUTTER_DESKTOP}`}
      >
        <span className="flex-1 text-[13px] font-semibold">
          Medcor · Emergency help
        </span>
        <span className="text-[11px] font-medium opacity-90">
          Step {step} of {STEP_COUNT}
        </span>
        <a
          href={`tel:${EMERGENCY_NUMBER}`}
          onClick={call112}
          aria-label={`Call ${EMERGENCY_NUMBER}, emergency services`}
          className="inline-flex items-center gap-1 rounded-full bg-bg-app px-2.5 py-1 text-[12px] font-semibold text-sos focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bg-app"
        >
          <Icon name="phone" size={12} /> {EMERGENCY_NUMBER}
        </a>
      </header>
      <PageBand
        className="flex flex-1 flex-col"
        contentClassName="flex flex-1 flex-col gap-4 px-4 py-5"
      >
        {children}
      </PageBand>
    </PageShell>
  )
}

/** The big red call button. The number is in the visible text too, for a
 * desktop where tel: does nothing (spec C0.3). */
export function Call112Button({
  label = `Call emergency services · ${EMERGENCY_NUMBER}`,
}: {
  label?: string
}): ReactNode {
  return (
    <a
      href={`tel:${EMERGENCY_NUMBER}`}
      onClick={call112}
      className="flex items-center justify-center gap-2 rounded-r3 bg-sos px-4 py-3 text-[15px] font-semibold text-sos-bg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sos"
    >
      <Icon name="phone" size={18} /> {label}
    </a>
  )
}

/** An inline 112 link inside running text. */
export function Inline112(): ReactNode {
  return (
    <a
      href={`tel:${EMERGENCY_NUMBER}`}
      onClick={call112}
      className="font-semibold text-sos underline underline-offset-2"
    >
      {EMERGENCY_NUMBER}
    </a>
  )
}

export function BackLink({
  to,
  children,
}: {
  to: string
  children: ReactNode
}): ReactNode {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1 self-start rounded text-[13px] font-medium text-ink-2 hover:text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
    >
      <Icon name="chevl" size={14} /> {children}
    </Link>
  )
}
